import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'

import { debounce } from '@livechat/data-utils'

import ColorCircle from './ColorCircle'
import FieldError from 'components/fields/FieldError'
import Input from 'components/fields/Input'
import { ColorPickerIcon, TickIcon } from 'components/icons'
import { getWCAGTextColor } from 'lib/WCAGContrast'
import { isValidHexColor } from 'lib/helpers'

const Wrapper = styled.div`
	position: relative;
	min-height: 40px;
	max-height: 40px;
	min-width: 40px;
`

const CustomColorCircle = styled(ColorCircle)`
	position: absolute;
	top: 0;
	left: 0;
	z-index: ${({ theme }) => theme.zIndexes.base};
`

const HiddenInput = styled.input`
	position: absolute;
	width: 100%;
	height: 100%;
	opacity: 0;
	top: 0;
	left: 0;
	z-index: ${({ theme }) => theme.zIndexes.above};
	cursor: pointer;

	&:focus-visible + label {
		outline: 1px solid ${({ theme }) => theme.colors.light.accent};
		outline-offset: 2px;
	}
`

const TextInput = styled(Input)`
	position: absolute;
	left: 16px;
	top: 1px;
	min-width: 116px;
	height: 38px;
	padding-left: 28px;
	text-transform: uppercase;
	z-index: ${({ theme }) => theme.zIndexes.none};
`

const Error = styled(FieldError)`
	bottom: -18px;
	left: 8px;
	min-width: 130px;
`

const PickerIcon = styled(ColorPickerIcon)`
	position: absolute;
	width: 28px;
	height: 28px;
	top: 6px;
	left: 6px;
	padding: ${({ theme }) => theme.spaces.space1};
	background-color: ${({ theme }) => theme.colors.light.surface};
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	z-index: ${({ theme }) => theme.zIndexes.base};
`

type Props = {
	value?: string
	onChange: (customColor: string) => void
}

const ColorInput: React.FC<Props> = ({ value, onChange }) => {
	const onChangeDebounced = React.useMemo(() => debounce(50, onChange), [onChange])
	const theme = useTheme()
	const isValid = value && isValidHexColor(value)
	const iconColor = isValid
		? getWCAGTextColor(theme.colorPalette.grayscale[0], theme.colorPalette.grayscale[900], value)
		: theme.colorPalette.grayscale[0]

	return (
		<Wrapper>
			<HiddenInput type="color" value={value ?? ''} onChange={(event) => onChangeDebounced(event.target.value)} />
			<CustomColorCircle fill={isValid ? value : undefined}>
				{!!value && <TickIcon color={iconColor} />}
			</CustomColorCircle>
			{(!value || (value && !isValid)) && <PickerIcon />}
			{value && <TextInput label="" value={value} onChange={(event) => onChange(event.target.value)} />}
			{value && !isValid && <Error>Provide valid hex color</Error>}
		</Wrapper>
	)
}

export default ColorInput
